import { ArrowsHorizontal, PencilLine, Users } from "@phosphor-icons/react";
import { ColumnDef } from "@tanstack/react-table";
import {
  ArcElement,
  Chart as ChartJS,
  Tooltip as ChartTooltip,
  Legend,
  type TooltipItem,
} from "chart.js";
import dayjs from "dayjs";
import { Badge, Button, ListGroup, TextInput, Tooltip } from "flowbite-react";
import { Pencil, MagnifyingGlass, PaperPlaneTilt, Trash, UserPlus } from "@phosphor-icons/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Doughnut } from "react-chartjs-2";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import Select from "react-select";
import AnimatedModal from "../../../../components/AnimatedModal";
import { Table } from "../../../../components/Table";
import { useAuth } from "../../../../hooks";
import { useTeamMemberDelete } from "../../../../hooks/delete/useTeamMemberDelete";
import { useTeamMembers } from "../../../../hooks/get/useTeamMembers";
import {
  ITeamMemberSessionItem,
  useTeamMemberSessions,
} from "../../../../hooks/get/useTeamMemberSessions";
import {
  UpdateTeamMemberParams,
  useTeamMemberUpdate,
} from "../../../../hooks/patch/useTeamMemberUpdate";
import { useTeamInvite } from "../../../../hooks/post/useTeamInvite";
import { checkPermission } from "../../../../lib/utils";
import {
  DEFAULT_ITEMS_PER_PAGE,
  PERMISSION_MAPPER,
  customSelectStyles,
} from "../../../../shared/constants";
import InviteTeamMemberModal from "../components/InviteTeamMemberModal";
import { ITeamMember } from "../interface";
ChartJS.register(ArcElement, ChartTooltip, Legend);

type RoleFilterOption = { value: string | number; label: string };

const TeamManagement: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [actionType, setActionType] = useState<
    "invite" | "reinvite" | "delete" | "update"
  >();
  const [selectedMember, setSelectedMember] = useState<ITeamMember>();
  const [showSessionsModal, setShowSessionsModal] = useState(false);
  const [selectedSession, setSelectedSession] =
    useState<ITeamMemberSessionItem | null>(null);
  const [searchValue, setSearchValue] = useState("");
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: DEFAULT_ITEMS_PER_PAGE,
    total: 1,
    from: 1,
    to: 1,
  });
  const [roleFilter, setRoleFilter] = useState<string | number>("all");

  const hasTeamMemberInvitePermission = useMemo(() => {
    return checkPermission(
      PERMISSION_MAPPER.TEAM_MANAGEMENT_CREATE,
      "modules",
      user?.user_permissions
    );
  }, [user?.user_permissions]);

  // Use our custom hooks
  const { data: teamMembersData, isLoading } = useTeamMembers({
    page: pagination.current_page,
    per_page: pagination.per_page,
  });

  const inviteMutation = useTeamInvite();
  const updateMutation = useTeamMemberUpdate();
  const deleteMutation = useTeamMemberDelete();

  const { results: teamMembers = [] } = teamMembersData || {};

  const selectedMemberUserId = useMemo(() => {
    if (!selectedMember) return undefined;
    // Prefer nested user id if available, else fallback to member id
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (selectedMember as any)?.user?.id ?? (selectedMember as any)?.id;
  }, [selectedMember]);

  const { data: sessionsData, isLoading: isSessionsLoading } =
    useTeamMemberSessions(
      { user_id: selectedMemberUserId ?? "" },
      showSessionsModal && !!selectedMemberUserId
    );

  // Auto-select first session on modal open if none is selected
  useEffect(() => {
    if (!showSessionsModal) return;
    const first = sessionsData?.results?.[0];
    if (first && !selectedSession) {
      setSelectedSession(first);
    }
  }, [showSessionsModal, sessionsData, selectedSession]);

  // Handle auto-popup for invite
  useEffect(() => {
    const popup = searchParams.get("popup");
    if (popup === "true" && hasTeamMemberInvitePermission) {
      setIsModalOpen(true);
      setActionType("invite");
      // Optional: Clear the param after opening to avoid re-opening on re-renders if needed, 
      // but usually navigating away or state change handles it.
    }
  }, [searchParams, hasTeamMemberInvitePermission]);

  const handleInviteTeamMember = (email: string, roleId: number) => {
    const payload = { email, roleId };

    if (actionType === "invite") {
      inviteMutation.mutate(payload, {
        onSuccess: () => {
          closeModal();
          toast.success(t("configurations.team.inviteSuccess"));
        },
        onError: (error: Error) => {
          closeModal();
          toast.error(error.message);
        },
      });
    } else {
      const updatePayload: UpdateTeamMemberParams = {
        memberId: String(selectedMember?.id),
      };

      if (actionType === "reinvite") {
        updatePayload.resend = true;
      } else if (roleId > 0) {
        updatePayload.roleId = roleId;
      } else {
        return;
      }

      updateMutation.mutate(updatePayload, {
        onSuccess: () => {
          closeModal();
          toast.success(t("configurations.team.updateSuccess"));
        },
        onError: (error: Error) => {
          closeModal();
          toast.error(error.message);
        },
      });
    }
  };
  const handlePermanentDelete = () => {
    if (!selectedMember?.id) return;
    deleteMutation.mutate(String(selectedMember.id), {
      onSuccess: () => {
        closeModal();
        toast.success(t("configurations.team.deleteSuccess"));
      },
      onError: (error: Error) => {
        closeModal();
        toast.error(error.message);
      },
    });
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedMember(undefined);
    setActionType(undefined);
  };


  const hasTeamMemberResendInvitePermission = useMemo(() => {
    return checkPermission(
      PERMISSION_MAPPER.TEAM_MANAGEMENT_CREATE,
      "modules",
      user?.user_permissions
    );
  }, [user?.user_permissions]);

  const hasTeamMemberUpdatePermission = useMemo(() => {
    return checkPermission(
      PERMISSION_MAPPER.TEAM_MANAGEMENT_UPDATE,
      "modules",
      user?.user_permissions
    );
  }, [user?.user_permissions]);

  const hasTeamMemberDeletePermission = useMemo(() => {
    return checkPermission(
      PERMISSION_MAPPER.TEAM_MANAGEMENT_DELETE,
      "modules",
      user?.user_permissions
    );
  }, [user?.user_permissions]);

  const hasEditRolePermission = useMemo(() => {
    return checkPermission(
      PERMISSION_MAPPER.ROLE_MANAGEMENT_UPDATE,
      "modules",
      user?.user_permissions
    );
  }, [user?.user_permissions]);

  const handleRoleClick = useCallback(
    (roleId: number) => {
      navigate(`/role-management/${roleId}`);
    },
    [navigate]
  );

  const columns = useMemo<ColumnDef<ITeamMember>[]>(
    () => [
      {
        accessorKey: "user_name",
        size: 150,
        header: ({ column }) => (
          <div
            className="flex items-center cursor-pointer"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("configurations.team.name")}
          </div>
        ),
        cell: ({ row }) => (
          <div className="flex items-center gap-3 relative">
            {row.original.user?.profile_picture ? (
              <img
                src={row.original.user.profile_picture}
                alt={
                  row.original.user.name ||
                  row.original.user.username ||
                  "Profile"
                }
                className="min-w-8 min-h-8 w-8 h-8 rounded-full object-cover border"
              />
            ) : (
              <div className="min-w-8 min-h-8 w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-400 border">
                <span className="text-xs">
                  {row.original.user?.name?.[0]?.toUpperCase() ||
                    row.original.user?.username?.[0]?.toUpperCase() ||
                    row.original?.email?.[0]?.toUpperCase() ||
                    "-"}
                </span>
              </div>
            )}
            <Tooltip
              content={
                row.original.user?.name || row.original.user?.username || "-"
              }
              theme={{
                target: "truncate",
              }}
            >
              {row.original.user?.name || row.original.user?.username || "-"}
            </Tooltip>
          </div>
        ),
      },
      {
        accessorKey: "email",
        size: 200,
        header: ({ column }) => (
          <div
            className="flex items-center cursor-pointer"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("configurations.team.email")}
          </div>
        ),
        cell: ({ row }) => (
          <span className="break-words">
            {row.original.user?.email || row.original.email}
          </span>
        ),
      },
      {
        id: "inviter",
        size: 200,
        header: t("configurations.team.invitedBy"),
        cell: ({ row }) => {
          const inviter = row.original.inviter;
          if (!inviter) return <span>-</span>;
          if (inviter.email === user?.email) return <span>You</span>;
          return (
            <div className="flex items-center gap-2">
              {inviter.profile_picture ? (
                <img
                  src={inviter.profile_picture}
                  alt={inviter.name || inviter.email}
                  className="min-w-8 min-h-8 w-8 h-8 rounded-full object-cover border"
                />
              ) : (
                <div className="min-w-8 min-h-8 w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-400 border text-xs">
                  {inviter.name?.[0]?.toUpperCase() ||
                    inviter.email?.[0]?.toUpperCase() ||
                    "-"}
                </div>
              )}
              <div className="flex flex-col">
                <Tooltip content={inviter.email}>{inviter.name || "-"}</Tooltip>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: "last_login",
        header: t("common.lastLogin"),
        size: 150,
        cell: ({ row }) =>
          row.original.user?.last_login ? (
            <span>
              {dayjs(row.original.user.last_login).format(
                "MMM D, YYYY, h:mm A"
              )}
            </span>
          ) : (
            <span>-</span>
          ),
      },
      {
        accessorKey: "role",
        size: 150,
        header: t("configurations.roles.role"),
        cell: ({ row }) =>
          !row.original.role?.name ||
            !hasEditRolePermission ||
            row.original.role?.is_default ? (
            <p>{row.original.role?.name || "-"}</p>
          ) : (
            <div
              className="text-indigo-600 cursor-pointer hover:underline flex items-center gap-2 group/role"
              onClick={() =>
                row.original.role?.id && handleRoleClick(row.original.role.id)
              }
            >
              <p className="leading-none">{row.original.role?.name || "-"}</p>
              <PencilLine
                size={16}
                weight="duotone"
                className="opacity-0 group-hover/role:opacity-100 transition-opacity"
              />
            </div>
          ),
      },
      {
        accessorKey: "status",
        size: 100,
        header: t("configurations.team.status"),
        cell: ({ row }) => {
          let color: "success" | "warning" | "failure" | "gray" = "gray";
          const statusKey = row.original.status?.toLowerCase();
          if (statusKey === "active") color = "success";
          else if (statusKey === "invited") color = "warning";
          else if (statusKey === "deleted") color = "failure";
          else if (statusKey === "deactive" || statusKey === "inactive")
            color = "gray";
          return (
            <Badge color={color} className="w-max capitalize">
              {t(`common.status.${statusKey}`)}
            </Badge>
          );
        },
      },
      ...(hasTeamMemberUpdatePermission ||
        hasTeamMemberResendInvitePermission ||
        hasTeamMemberDeletePermission
        ? [
          {
            id: "actions",
            size: 150,
            header: t("configurations.team.actions"),
            cell: ({ row }) => {
              return (
                <div className="flex gap-2 relative">
                  <Tooltip content={t("common.edit")}>
                    <Button
                      size="xs"
                      outline
                      color="dark"
                      disabled={
                        !hasTeamMemberUpdatePermission ||
                        row.original.is_owner ||
                        row.original?.email === user?.email
                      }
                      className="p-[2px]"
                      onClick={() => {
                        if (
                          hasTeamMemberUpdatePermission &&
                          !row.original.is_owner &&
                          row.original?.email !== user?.email
                        ) {
                          setIsModalOpen(true);
                          setSelectedMember(row.original);
                          setActionType("update");
                        }
                      }}
                    >
                      <Pencil className="h-5" size={14} />
                    </Button>
                  </Tooltip>
                  <Tooltip content={t("common.resend")}>
                    <Button
                      size="xs"
                      outline
                      disabled={
                        !hasTeamMemberResendInvitePermission ||
                        row.original.status === "active"
                      }
                      color="indigo"
                      className="p-[2px]"
                      onClick={() => {
                        if (
                          hasTeamMemberResendInvitePermission &&
                          row.original.status === "invited"
                        ) {
                          setIsModalOpen(true);
                          setSelectedMember(row.original);
                          setActionType("reinvite");
                        }
                      }}
                    >
                      <PaperPlaneTilt className="h-5" size={14} />
                    </Button>
                  </Tooltip>
                  {/* <Tooltip content={t("configurations.team.sessions.button")}>
                      <Button
                        size="xs"
                        outline
                        color="purple"
                        className="p-[2px]"
                        disabled={!hasTeamMemberResendInvitePermission}
                        onClick={() => {
                          if (hasTeamMemberResendInvitePermission) {
                            setSelectedMember(row.original);
                            setShowSessionsModal(true);
                          }
                        }}
                      >
                        <PieChart className="h-5" size={14} />
                      </Button>
                    </Tooltip> */}
                  <Tooltip content={t("common.delete")}>
                    <Button
                      size="xs"
                      outline
                      color="failure"
                      className="p-[2px]"
                      disabled={!hasTeamMemberDeletePermission}
                      onClick={() => {
                        if (hasTeamMemberDeletePermission) {
                          setIsModalOpen(true);
                          setSelectedMember(row.original);
                          setActionType("delete");
                        }
                      }}
                    >
                      <Trash className="h-5" size={14} />
                    </Button>
                  </Tooltip>
                </div>
              );
            },
          } as ColumnDef<ITeamMember>,
        ]
        : []),
    ],
    [
      t,
      handleRoleClick,
      hasTeamMemberUpdatePermission,
      user?.email,
      hasTeamMemberResendInvitePermission,
      hasTeamMemberDeletePermission,
      hasEditRolePermission,
    ]
  );

  // Update pagination when we receive new data
  useEffect(() => {
    if (teamMembersData) {
      const { ...paginationData } = teamMembersData;
      setPagination(paginationData);
    }
  }, [teamMembersData]);

  // Add the filter function
  const handleFilter = (member: ITeamMember) => {
    const search = searchValue.toLowerCase();
    // Gather all searchable fields
    const name = member.user?.name?.toLowerCase() || "";
    const email = (member.user?.email || member.email || "").toLowerCase();
    const username = member.user?.username?.toLowerCase() || "";
    const lastLogin = member.user?.last_login
      ? dayjs(member.user.last_login)
        .format("MMM D, YYYY, h:mm A")
        .toLowerCase()
      : "";
    const status = (member.status || "").toLowerCase();
    const roleName = (member.role?.name || "").toLowerCase();

    // Search match: any field contains the search string
    const matchesSearch =
      name.includes(search) ||
      email.includes(search) ||
      username.includes(search) ||
      lastLogin.includes(search) ||
      status.includes(search) ||
      roleName.includes(search);

    // Role filter
    if (roleFilter === "all") return matchesSearch;
    return (
      matchesSearch &&
      member.role &&
      String(member.role.id) === String(roleFilter)
    );
  };

  // Memoize unique role options from teamMembers
  const roleOptions = useMemo(() => {
    const uniqueRoles = Array.from(
      new Map(
        teamMembers
          .filter((member) => member.role && member.role.id && member.role.name)
          .map((member) => [
            member.role!.id,
            { value: member.role!.id, label: member.role!.name },
          ])
      ).values()
    );
    return [
      { value: "all", label: t("common.allRoles", "All Roles") },
      ...uniqueRoles,
    ];
  }, [teamMembers, t]);

  return (
    <div>
      {/* Mobile Header */}
      <div className="flex flex-col gap-4 sm:hidden">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Users size={20} weight={"duotone"} className="text-gray-700" />
            <h1 className="text-md font-bold text-gray-700">
              {t("configurations.team.title")}
            </h1>
          </div>
          {hasTeamMemberInvitePermission && (
            <Button
              color="primary"
              size="sm"
              onClick={() => {
                setIsModalOpen(true);
                setActionType("invite");
              }}
            >
              <UserPlus className="w-4 h-4" />
            </Button>
          )}
        </div>
        <TextInput
          id="search"
          type="text"
          icon={MagnifyingGlass}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          placeholder={t("common.search")}
          className="w-full"
        />
      </div>

      {/* Desktop Header */}
      <div className="hidden sm:flex sm:items-center gap-4 justify-between">
        <div className="flex items-center gap-2 pl-2">
          <Users size={20} weight={"duotone"} className="text-gray-700" />
          <h1 className="text-md font-bold text-gray-700">
            {t("configurations.team.title")}
          </h1>
        </div>
        <div className="flex gap-2">
          <TextInput
            id="search"
            type="text"
            icon={MagnifyingGlass}
            sizing={"md"}
            style={{ height: "38px" }}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder={t("common.search")}
          />
          <div style={{ minWidth: 200 }}>
            <Select
              options={roleOptions}
              value={roleOptions.find((opt) => opt.value === roleFilter)}
              classNamePrefix={"custom-select"}
              onChange={(opt) => {
                if (opt && typeof opt === "object" && "value" in opt) {
                  setRoleFilter(opt.value);
                } else {
                  setRoleFilter("all");
                }
              }}
              styles={customSelectStyles<RoleFilterOption>()}
              isMulti={false}
              isClearable={roleFilter !== "all"}
              isSearchable={false}
              menuPlacement="auto"
            />
          </div>
          {hasTeamMemberInvitePermission && (
            <Button
              size="sm"
              color="primary"
              onClick={() => {
                setIsModalOpen(true);
                setActionType("invite");
              }}
            >
              <div className="flex items-center gap-1">
                <UserPlus className="w-4 h-4" />
                {t("configurations.team.invite")}
              </div>
            </Button>
          )}
        </div>
      </div>

      {/* Table */}
      <Table
        data={teamMembers.filter(handleFilter)}
        columns={columns}
        isLoading={isLoading}
        pageSize={pagination.per_page}
        className="mt-2"
        loadingHeight={3.96}
        loadingRowCount={12}
        maxHeight={`calc(100vh - ${isLoading ? 148 : 209}px)`}
      />

      {/* Modals */}
      <InviteTeamMemberModal
        key={actionType}
        open={isModalOpen && ["invite", "update"].includes(actionType || "")}
        onClose={closeModal}
        onInvite={handleInviteTeamMember}
        selectedMember={selectedMember}
        isInviting={inviteMutation.isPending || updateMutation.isPending}
        existingMembers={teamMembers}
      />

      {/* Only show delete confirmation modal for delete action */}
      {isModalOpen && actionType === "delete" && (
        <AnimatedModal
          isConfirmation
          show={true}
          onClose={closeModal}
          onConfirm={handlePermanentDelete}
          isConfirming={deleteMutation.isPending}
          title={t("configurations.team.confirmDeleteMemberTitle")}
          children={
            <div className="text-gray-700 text-center">
              {t("configurations.team.confirmDeleteMemberContent")}
            </div>
          }
          confirmText={t("common.delete")}
          confirmButtonColor="red"
        />
      )}

      <AnimatedModal
        isConfirmation
        show={isModalOpen && actionType === "reinvite"}
        onClose={closeModal}
        isConfirming={updateMutation.isPending}
        onConfirm={() =>
          selectedMember
            ? handleInviteTeamMember(
              selectedMember.email,
              selectedMember.role?.id || -1
            )
            : null
        }
        title={t("configurations.team.confirmResendInviteTitle")}
        children={
          <div className="text-gray-700 text-center">
            {t("configurations.team.confirmResendInviteContent")}
          </div>
        }
        confirmText={t("common.resend")}
        confirmButtonColor="indigo"
      />

      {/* Sessions Modal */}
      {showSessionsModal && (
        <AnimatedModal
          show={showSessionsModal}
          size="2xl"
          onClose={() => {
            setShowSessionsModal(false);
            setSelectedSession(null);
          }}
          title={
            selectedMember?.user?.name
              ? t("configurations.team.sessions.titleWithUsername", {
                name: selectedMember?.user?.name,
              })
              : t("configurations.team.sessions.title")
          }
          children={
            <div className="flex flex-col sm:flex-row gap-4 border rounded-md p-2">
              {/* Left: sessions list */}
              <div className="sm:w-1/2 w-full max-h-[60vh] overflow-auto ">
                {isSessionsLoading ? (
                  <div className="text-center text-sm text-gray-500 py-8">
                    {t("configurations.team.sessions.loading")}
                  </div>
                ) : sessionsData && sessionsData.results.length > 0 ? (
                  <ListGroup>
                    {sessionsData.results.map((s) => {
                      const start = dayjs(s.start_time).format(
                        "MMM D, YYYY h:mm A"
                      );
                      const end = s.end_time
                        ? dayjs(s.end_time).format("MMM D, YYYY h:mm A")
                        : "";
                      const isActive = s.is_active;
                      const selected = selectedSession?.id === s.id;
                      return (
                        <ListGroup.Item
                          key={s.id}
                          onClick={() => setSelectedSession(s)}
                          className={`cursor-pointer ${selected ? "bg-green-100" : ""
                            }`}
                        >
                          <div className="flex flex-col items-start">
                            <div className="flex items-center gap-3 w-full">
                              <div className="flex items-center gap-2">
                                <div className="text-sm font-medium text-gray-800">
                                  {start}
                                </div>
                                {end ? (
                                  <>
                                    <ArrowsHorizontal />
                                    <div className="text-sm font-medium text-gray-800">
                                      {end}
                                    </div>
                                  </>
                                ) : null}
                              </div>
                              <div className="flex items-center gap-2">
                                {isActive ? (
                                  <Badge color="success">
                                    {t("configurations.team.sessions.active")}
                                  </Badge>
                                ) : null}
                              </div>
                            </div>
                            <div className="flex items-center gap-1 mt-1 text-xs  text-gray-500">
                              {t("configurations.team.sessions.ip")}:
                              <Badge color="indigo">
                                {s.ip_address || "-"}
                              </Badge>
                            </div>
                          </div>
                        </ListGroup.Item>
                      );
                    })}
                  </ListGroup>
                ) : (
                  <div className="text-center text-sm text-gray-500 py-8">
                    {t("configurations.team.sessions.noSessions")}
                  </div>
                )}
              </div>

              {/* Right: pie chart */}
              <div className="sm:w-1/2 w-full p-4 flex flex-col items-center justify-center">
                {(() => {
                  const s = selectedSession || sessionsData?.results?.[0];
                  if (!s) {
                    return (
                      <div className="text-sm text-gray-500">
                        {t("configurations.team.sessions.selectSession")}
                      </div>
                    );
                  }
                  const upMin = Math.max(0, s.uptime_minutes || 0);
                  const downMin = Math.max(0, s.downtime_minutes || 0);
                  const hasAny = upMin + downMin > 0;
                  const formatHM = (mins: number) => {
                    const h = Math.floor(mins / 60);
                    const m = Math.round(mins % 60);
                    return `${h}h ${m}m`;
                  };
                  const labelActive = t("configurations.team.sessions.active");
                  const labelInactive = t(
                    "configurations.team.sessions.inactive"
                  );
                  // Use minutes for dataset to avoid precision loss; format in tooltips/labels as h m
                  const data = hasAny
                    ? {
                      labels: [labelActive, labelInactive],
                      datasets: [
                        {
                          data: [upMin, downMin],
                          backgroundColor: ["#10b981", "#e5e7eb"],
                          borderColor: ["#10b981", "#e5e7eb"],
                          borderWidth: 1,
                        },
                      ],
                    }
                    : {
                      labels: [t("common.noData")],
                      datasets: [
                        {
                          data: [1],
                          backgroundColor: ["#e5e7eb"],
                          borderColor: ["#e5e7eb"],
                          borderWidth: 1,
                        },
                      ],
                    };
                  const options = {
                    responsive: true,
                    cutout: "70%",
                    plugins: {
                      legend: { position: "bottom" as const },
                      tooltip: {
                        enabled: hasAny,
                        callbacks: {
                          label: (ctx: TooltipItem<"doughnut">) =>
                            `${ctx.label}: ${formatHM(Number(ctx.raw))}`,
                        },
                      },
                    },
                  } as const;
                  return (
                    <div className="w-full flex flex-col items-center gap-4">
                      <div className="relative w-[240px] h-[240px]">
                        <Doughnut data={data} options={options} />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="text-center mb-5">
                            <div className="text-xs text-gray-500">
                              {t("configurations.team.sessions.totalHours")}
                            </div>
                            <div className="text-lg font-semibold">
                              {formatHM(upMin + downMin)}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-6">
                        <div className="flex items-center gap-2">
                          <span
                            className="w-3 h-3 rounded-sm"
                            style={{ background: "#10b981" }}
                          />
                          <span className="text-sm text-gray-700">{`${labelActive}: ${formatHM(
                            upMin
                          )}`}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span
                            className="w-3 h-3 rounded-sm"
                            style={{ background: "#e5e7eb" }}
                          />
                          <span className="text-sm text-gray-700">{`${labelInactive}: ${formatHM(
                            downMin
                          )}`}</span>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 flex items-center gap-2 min-h-[20px]">
                        <span>{`${t(
                          "configurations.team.sessions.start"
                        )}: ${dayjs(s.start_time).format(
                          "MMM D, YYYY h:mm A"
                        )}`}</span>
                        <ArrowsHorizontal />
                        <span>
                          {s.end_time ? (
                            ` · ${t(
                              "configurations.team.sessions.end"
                            )}: ${dayjs(s.end_time).format(
                              "MMM D, YYYY h:mm A"
                            )}`
                          ) : (
                            <Badge color="success">{labelActive}</Badge>
                          )}
                        </span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          }
          confirmText={undefined as unknown as string}
          isConfirmation={false}
        />
      )}
    </div>
  );
};

export default TeamManagement;
